// Pure consent helpers — no IO (cookie read/write lives in the component). Script gating via hasConsent() consumed by T39/T55.

export const CONSENT_VERSION = "1";
export const CONSENT_COOKIE = "tfc_consent";

/**
 * T78 — fired on `window` whenever a consent choice is persisted, so listeners can react
 * without a page reload. Lives HERE, beside the cookie it announces, rather than in the
 * component that dispatches it or the one that listens: this module is already the single
 * source of truth for consent, and a `components/ui` primitive should not have to import
 * from a feature provider to know the name of the event it emits.
 *
 * Deliberately payload-free — listeners re-read the cookie, so a malformed dispatch
 * cannot desync them from the SSOT.
 */
export const CONSENT_CHANGE_EVENT = "tfc:consent-change";

/**
 * T78 — whether analytics may fire for a given request, server-side.
 *
 * Read from the `tfc_consent` cookie the browser already sends, **never** from a field in
 * a request body: a client-supplied "yes I consented" is not evidence of consent. Lives
 * here rather than in whichever route needed it first, because it now has two callers
 * (`/api/leads` and the bonus-download loader) and a second copy is how the two would
 * eventually disagree about what consent means.
 *
 * Pure: parses a header, touches no IO, so it belongs in `app/lib`.
 */
export function analyticsConsentedFromRequest(request: Request): boolean {
  return hasConsent("analytics", parseConsent(readRawConsent(request.headers.get("cookie") ?? undefined)));
}

/** 1 year — the consent expiry mandated by the spec. */
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export type ConsentCategory = "analytics" | "marketing";

export interface ConsentState {
  version: string;
  /** ISO 8601 — when the choice was recorded (Art. 5.2 accountability). */
  timestamp: string;
  analytics: boolean;
  marketing: boolean;
}

export function makeConsent(
  choices: { analytics: boolean; marketing: boolean },
  now: string = new Date().toISOString(),
): ConsentState {
  return {
    version: CONSENT_VERSION,
    timestamp: now,
    analytics: choices.analytics,
    marketing: choices.marketing,
  };
}

/** Essential-only baseline (no opt-in). The default toggle state before a choice exists. */
export function defaultConsent(now: string = new Date().toISOString()): ConsentState {
  return makeConsent({ analytics: false, marketing: false }, now);
}

/** Extract the raw `tfc_consent` value from a `document.cookie` string. */
export function readRawConsent(cookieString: string | undefined): string | undefined {
  if (!cookieString) return undefined;
  for (const part of cookieString.split(";")) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf("=");
    if (eq === -1) continue;
    if (trimmed.slice(0, eq) === CONSENT_COOKIE) return trimmed.slice(eq + 1);
  }
  return undefined;
}

/**
 * Parse + validate a stored consent value. Returns null when absent, malformed,
 * or recorded under a stale schema version — any of which re-prompts the banner.
 */
export function parseConsent(raw: string | undefined): ConsentState | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const o = parsed as Record<string, unknown>;
  if (o.version !== CONSENT_VERSION) return null;
  if (typeof o.timestamp !== "string") return null;
  if (typeof o.analytics !== "boolean" || typeof o.marketing !== "boolean") return null;
  return {
    version: o.version,
    timestamp: o.timestamp,
    analytics: o.analytics,
    marketing: o.marketing,
  };
}

export function serializeConsent(state: ConsentState): string {
  return encodeURIComponent(JSON.stringify(state));
}

/** Build the full string to assign to `document.cookie`. */
export function buildConsentCookie(
  state: ConsentState,
  opts: { secure?: boolean } = {},
): string {
  const attrs = [
    `${CONSENT_COOKIE}=${serializeConsent(state)}`,
    "Path=/",
    `Max-Age=${CONSENT_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  if (opts.secure) attrs.push("Secure");
  return attrs.join("; ");
}

export function hasConsent(category: ConsentCategory, state: ConsentState | null): boolean {
  return state ? state[category] === true : false;
}

/**
 * Server-side gate: does a request's `Cookie` header carry a valid analytics
 * grant? Absent/malformed/stale-version cookies all read as "no consent" —
 * the same fail-closed posture as the banner re-prompt.
 */
export function hasServerAnalyticsConsent(cookieHeader: string | null | undefined): boolean {
  return hasConsent("analytics", parseConsent(readRawConsent(cookieHeader ?? undefined)));
}
